import type { FormAssignmentsResponse } from './api/types';

export type FormReminderChannel = 'sms' | 'email';

const CHANNELS: FormReminderChannel[] = ['sms', 'email'];

export interface FormReminderTarget {
  submissionId?: string;
  assignmentId?: string;
}

/**
 * Send the form link by text and email.
 * A submitted form uses the submission resend. A form that was sent but not
 * opened yet has no submission, so the assignment is used instead.
 */
export async function deliverFormReminder(
  target: FormReminderTarget,
  api: {
    resendSubmission: (submissionId: string, channel: FormReminderChannel) => Promise<unknown>;
    resendAssignment: (assignmentId: string, channel: FormReminderChannel) => Promise<unknown>;
  },
): Promise<void> {
  if (target.submissionId) {
    await Promise.all(CHANNELS.map((channel) => api.resendSubmission(target.submissionId!, channel)));
    return;
  }
  if (target.assignmentId) {
    await Promise.all(CHANNELS.map((channel) => api.resendAssignment(target.assignmentId!, channel)));
    return;
  }
  throw new Error('This form has not been sent to the patient yet.');
}

/** Patient-forms rows identify the template; the assignment list has the id we can remind. */
export function reminderTargetForPatientForm(
  form: { id: string; assignmentId?: string; submissionId?: string },
  assignments: FormAssignmentsResponse | undefined,
  patientId: number | null,
): FormReminderTarget {
  const listed = (assignments?.results ?? []).find((item) => {
    if (form.assignmentId && item.assignment_id === form.assignmentId) return true;
    if (patientId == null || item.form_id !== form.id || item.patient_id !== patientId) return false;
    return (
      item.assignment_status === 'pending' ||
      item.assignment_status === 'in_progress' ||
      item.assignment_status === 'submitted'
    );
  });
  const submissionId = form.submissionId ?? listed?.submission_id ?? undefined;
  const assignmentId = form.assignmentId ?? listed?.assignment_id;
  return {
    ...(submissionId ? { submissionId } : {}),
    ...(assignmentId ? { assignmentId } : {}),
  };
}
